import { alpha, createTheme } from "@mui/material/styles";

const THEME_PRESETS = {
  "elite-red": {
    brand: {
      50: "#FFF3F3",
      100: "#FFE3E3",
      200: "#FFC9C9",
      300: "#FF9F9F",
      400: "#F46868",
      500: "#E23333",
      600: "#C70909",
      700: "#A30707",
      800: "#830505",
      900: "#650404",
    },
  },
  blue: {
    brand: {
      50: "#F2F7FF",
      100: "#E0ECFF",
      200: "#BFD6FF",
      300: "#90B7FF",
      400: "#5E96FF",
      500: "#2F76F3",
      600: "#1459D9",
      700: "#1047AE",
      800: "#0D388A",
      900: "#0A2B69",
    },
  },
};

const ACTIVE_THEME_PRESET = THEME_PRESETS[process.env.REACT_APP_THEME_PRESET]
  ? process.env.REACT_APP_THEME_PRESET
  : "elite-red";

const tokens = {
  color: {
    brand: THEME_PRESETS[ACTIVE_THEME_PRESET].brand,
    project: {
      journey_btn: "#ffebb3",
      docs_btn: "#acdceb",
      cart_btn: "#afefaf",
    },
    neutral: {
      0: "#FFFFFF",
      25: "#FCFCFD",
      50: "#F8FAFC",
      100: "#F1F5F9",
      200: "#E2E8F0",
      300: "#CBD5E1",
      400: "#94A3B8",
      500: "#64748B",
      600: "#475569",
      700: "#334155",
      800: "#1F2937",
      900: "#0F172A",
    },
    success: "#0F766E",
    warning: "#B45309",
    error: "#B42318",
    info: "#1D4ED8",
  },
  radius: {
    xs: 8,
    sm: 10,
    md: 14,
    lg: 18,
    xl: 24,
  },
  shadow: {
    xs: "0 1px 2px rgba(15, 23, 42, 0.06)",
    sm: "0 4px 12px rgba(15, 23, 42, 0.06)",
    md: "0 10px 30px rgba(15, 23, 42, 0.08)",
    lg: "0 20px 44px rgba(15, 23, 42, 0.12)",
  },
};

const baseTheme = createTheme({
  spacing: 8,
  palette: {
    mode: "light",
    primary: {
      light: tokens.color.brand[400],
      main: tokens.color.brand[600],
      dark: tokens.color.brand[700],
      contrastText: tokens.color.neutral[0],
    },
    secondary: {
      main: tokens.color.neutral[700],
      contrastText: tokens.color.neutral[0],
    },
    background: {
      default: tokens.color.neutral[50],
      paper: tokens.color.neutral[0],
    },
    text: {
      primary: tokens.color.neutral[900],
      secondary: tokens.color.neutral[500],
    },
    divider: tokens.color.neutral[200],
    success: {
      main: tokens.color.success,
    },
    warning: {
      main: tokens.color.warning,
    },
    error: {
      main: tokens.color.error,
    },
    info: {
      main: tokens.color.info,
    },
  },
  shape: {
    borderRadius: tokens.radius.md,
  },
  typography: {
    fontFamily: '"Nunito", "Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: "2.5rem",
      lineHeight: 1.08,
      letterSpacing: "-0.03em",
    },
    h2: {
      fontWeight: 700,
      fontSize: "2.125rem",
      lineHeight: 1.1,
      letterSpacing: "-0.025em",
    },
    h3: {
      fontWeight: 700,
      fontSize: "1.75rem",
      lineHeight: 1.12,
      letterSpacing: "-0.02em",
    },
    h4: {
      fontWeight: 700,
      fontSize: "1.5rem",
      lineHeight: 1.14,
      letterSpacing: "-0.015em",
    },
    h5: {
      fontWeight: 700,
      fontSize: "1.25rem",
      lineHeight: 1.2,
      letterSpacing: "-0.01em",
    },
    h6: {
      fontWeight: 700,
      fontSize: "1rem",
      lineHeight: 1.25,
    },
    subtitle1: {
      fontSize: "0.95rem",
      fontWeight: 600,
    },
    subtitle2: {
      fontSize: "0.8125rem",
      fontWeight: 700,
    },
    body1: {
      fontSize: "0.9375rem",
      lineHeight: 1.55,
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.5,
    },
    caption: {
      fontSize: "0.75rem",
      lineHeight: 1.45,
    },
    overline: {
      fontSize: "0.6875rem",
      fontWeight: 800,
      lineHeight: 1.4,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
    },
    button: {
      fontWeight: 600,
      fontSize: "0.875rem",
      lineHeight: 1.2,
      letterSpacing: "-0.01em",
      textTransform: "none",
    },
  },
});

const theme = createTheme(baseTheme, {
  eliteCore: {
    tokens,
    brandPreset: ACTIVE_THEME_PRESET,
    presets: Object.keys(THEME_PRESETS),
    surface: {
      subtle: tokens.color.neutral[25],
      muted: tokens.color.neutral[100],
      strongBorder: tokens.color.neutral[300],
    },
    shadows: {
      card: tokens.shadow.sm,
      panel: tokens.shadow.md,
      overlay: tokens.shadow.lg,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ":root": {
          "--ec-color-primary": tokens.color.brand[600],
          "--ec-color-primary-dark": tokens.color.brand[700],
          "--ec-color-primary-soft": tokens.color.brand[50],
          "--ec-color-primary-soft-strong": alpha(tokens.color.brand[600], 0.08),
          "--ec-color-primary-border-soft": alpha(tokens.color.brand[600], 0.16),
          "--ec-color-primary-focus": alpha(tokens.color.brand[600], 0.12),
          "--ec-color-bg": tokens.color.neutral[50],
          "--ec-color-surface": tokens.color.neutral[0],
          "--ec-color-surface-muted": tokens.color.neutral[100],
          "--ec-color-border": tokens.color.neutral[200],
          "--ec-color-border-strong": tokens.color.neutral[300],
          "--ec-color-text": tokens.color.neutral[900],
          "--ec-color-text-muted": tokens.color.neutral[500],
          "--ec-color-text-muted-strong": tokens.color.neutral[600],
          "--ec-radius-sm": `${tokens.radius.sm}px`,
          "--ec-radius-md": `${tokens.radius.md}px`,
          "--ec-radius-lg": `${tokens.radius.lg}px`,
          "--ec-shadow-xs": tokens.shadow.xs,
          "--ec-shadow-sm": tokens.shadow.sm,
          "--ec-shadow-md": tokens.shadow.md,

          "--ec-color-button-bg-1": tokens.color.project.journey_btn, 
          "--ec-color-button-bg-2": tokens.color.project.docs_btn, 
          "--ec-color-button-bg-3": tokens.color.project.cart_btn, 

          "--ec-status-warning-bg": alpha(tokens.color.warning, 0.10),
          "--ec-status-warning-text": tokens.color.warning,
          "--ec-status-warning-border": alpha(tokens.color.warning, 0.22),

          "--ec-status-info-bg": alpha(tokens.color.info, 0.10),
          "--ec-status-info-text": tokens.color.info,
          "--ec-status-info-border": alpha(tokens.color.info, 0.22),

          "--ec-status-success-bg": alpha(tokens.color.success, 0.10),
          "--ec-status-success-text": tokens.color.success,
          "--ec-status-success-border": alpha(tokens.color.success, 0.22),

          "--ec-status-error-bg": alpha(tokens.color.error, 0.10),
          "--ec-status-error-text": tokens.color.error,
          "--ec-status-error-border": alpha(tokens.color.error, 0.22),

          "--ec-status-neutral-bg": tokens.color.neutral[100],
          "--ec-status-neutral-text": tokens.color.neutral[600],
          "--ec-status-neutral-border": tokens.color.neutral[200],

        
          "--ec-page-bg":         tokens.color.neutral[50],
          "--ec-sidebar-bg":      tokens.color.neutral[0],
          "--ec-header-accent":   tokens.color.brand[600],   

        
          "--ec-toolbar-bg":      tokens.color.neutral[0],
          "--ec-toolbar-border":  tokens.color.neutral[200],

        
          "--ec-tab-bg":          tokens.color.neutral[100],
          "--ec-tab-active-bg":   tokens.color.brand[600],
          "--ec-tab-active-text": tokens.color.neutral[0],
          "--ec-tab-text":        tokens.color.neutral[600],
          "--ec-tab-hover-bg":    tokens.color.neutral[900],
          "--ec-tab-hover-text":  tokens.color.neutral[0],

        
          "--ec-modal-header-bg":   tokens.color.brand[600],
          "--ec-modal-header-text": tokens.color.neutral[0],
          "--ec-modal-close-bg":    tokens.color.neutral[900],

        
          "--ec-table-head-bg":     tokens.color.neutral[100],
          "--ec-table-head-bg-strong": tokens.color.neutral[800],
          "--ec-table-head-bg-hover": tokens.color.neutral[700],
          "--ec-table-head-text":   tokens.color.neutral[600],
          "--ec-table-head-text-inverse": tokens.color.neutral[50],
          "--ec-table-head-text-muted": tokens.color.neutral[400],
          "--ec-table-row-hover":   tokens.color.neutral[50],
          "--ec-table-border":      tokens.color.neutral[200],

        
          "--ec-input-bg":          tokens.color.neutral[100],
          "--ec-input-border":      tokens.color.neutral[300],
          "--ec-input-focus-ring": `0 0 0 4px ${alpha(tokens.color.brand[600], 0.12)}`,
        
          "--ec-btn-primary-bg":    tokens.color.brand[600],
          "--ec-btn-primary-hover": tokens.color.brand[700],
          "--ec-btn-secondary-bg":  tokens.color.neutral[900],
          "--ec-btn-secondary-hover": tokens.color.neutral[700],

        },
        html: {
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        },
        body: {
          margin: 0,
          backgroundColor: tokens.color.neutral[50],
          color: tokens.color.neutral[900],
        },
        "#elite-core": {
          minHeight: "100vh",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          minHeight: 38,
          borderRadius: tokens.radius.sm,
          paddingInline: 16,
          fontWeight: 600,
          transition: baseTheme.transitions.create(["background-color", "box-shadow", "transform", "border-color"], {
            duration: baseTheme.transitions.duration.shorter,
          }),
        },
        containedPrimary: {
          backgroundColor: tokens.color.brand[600],
          boxShadow: `0 1px 2px rgba(15, 23, 42, 0.08), 0 1px 3px ${alpha(tokens.color.brand[600], 0.14)}`,
          "&:hover": {
            backgroundColor: tokens.color.brand[700],
            boxShadow: `0 4px 10px rgba(15, 23, 42, 0.08), 0 2px 6px ${alpha(tokens.color.brand[700], 0.16)}`,
            transform: "translateY(-1px)",
          },
          "&:active": {
            backgroundColor: tokens.color.brand[700],
            boxShadow: `0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 4px ${alpha(tokens.color.brand[700], 0.14)}`,
            transform: "translateY(0)",
          },
          "&.Mui-focusVisible": {
            boxShadow: `0 0 0 4px ${alpha(tokens.color.brand[600], 0.12)}, 0 1px 2px rgba(15, 23, 42, 0.08)`,
          },
        },
        outlined: {
          borderColor: tokens.color.neutral[300],
          color: tokens.color.neutral[700],
          "&:hover": {
            borderColor: tokens.color.neutral[400],
            backgroundColor: tokens.color.neutral[25],
          },
        },
        text: {
          color: tokens.color.neutral[700],
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.sm,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        rounded: {
          borderRadius: tokens.radius.lg,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.lg,
          border: `1px solid ${tokens.color.neutral[200]}`,
          boxShadow: tokens.shadow.sm,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: 40,
          borderRadius: tokens.radius.sm,
          backgroundColor: tokens.color.neutral[0],
          transition: baseTheme.transitions.create(["border-color", "box-shadow", "background-color"], {
            duration: baseTheme.transitions.duration.shorter,
          }),
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: tokens.color.neutral[300],
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: tokens.color.neutral[400],
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: tokens.color.brand[600],
            borderWidth: 1,
          },
          "&.Mui-focused": {
            boxShadow: `0 0 0 4px ${alpha(tokens.color.brand[600], 0.08)}`,
          },
        },
        input: {
          paddingTop: 10,
          paddingBottom: 10,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: tokens.color.neutral[500],
          "&.Mui-focused": {
            color: tokens.color.brand[600],
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
        },
        indicator: {
          height: 3,
          borderRadius: 999,
          backgroundColor: tokens.color.brand[600],
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 40,
          minWidth: 88,
          paddingInline: 14,
          fontWeight: 700,
          fontSize: "0.875rem",
          textTransform: "none",
          color: tokens.color.neutral[500],
          "&.Mui-selected": {
            color: tokens.color.neutral[900],
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 600,
        },
        outlined: {
          borderColor: tokens.color.neutral[300],
        },
        outlinedPrimary: {
          borderColor: alpha(tokens.color.brand[600], 0.18),
          color: tokens.color.brand[700],
          backgroundColor: alpha(tokens.color.brand[600], 0.05),
        },
        filledPrimary: {
          backgroundColor: alpha(tokens.color.brand[600], 0.1),
          color: tokens.color.brand[700],
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radius.lg,
          border: `1px solid ${tokens.color.neutral[200]}`,
          boxShadow: tokens.shadow.lg,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: tokens.color.neutral[100],
          color: tokens.color.neutral[600],
          fontWeight: 700,
          fontSize: "0.75rem",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        },
        root: {
          borderColor: tokens.color.neutral[200],
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:last-child td, &:last-child th": {
            borderBottom: 0,
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          // borderRadius: tokens.radius.lg,
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        toolbar: {
          minHeight: 48,
        },
        selectLabel: {
          color: tokens.color.neutral[500],
        },
        displayedRows: {
          color: tokens.color.neutral[500],
        },
        select: {
          color: tokens.color.neutral[500],
        },
        actions: {
          "& .MuiIconButton-root": {
            color: `${tokens.color.neutral[500]} !important`,
          },
          "& .MuiIconButton-root svg": {
            fill: `${tokens.color.neutral[500]} !important`,
          },
          "& .MuiIconButton-root.Mui-disabled svg": {
            fill: `${tokens.color.neutral[300]} !important`,
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        grouped: {
          borderRadius: tokens.radius.lg,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: tokens.color.neutral[600],
          borderColor: tokens.color.neutral[300],
          textTransform: "none",
          "&.Mui-selected": {
            backgroundColor: tokens.color.brand[600],
            color: tokens.color.neutral[0],
            "&:hover": {
              backgroundColor: tokens.color.brand[700],
            },
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: tokens.radius.sm,
          backgroundColor: tokens.color.neutral[900],
          fontSize: "0.75rem",
        },
      },
    },


 
MuiDataGrid: {
  styleOverrides: {
    root: {
      border: `1px solid ${tokens.color.neutral[200]}`,
      borderRadius: tokens.radius.lg,
      fontFamily: '"Nunito", sans-serif',
      backgroundColor: tokens.color.neutral[0],
    },
    columnHeaders: {
      backgroundColor: tokens.color.neutral[100],
      borderBottom: `1px solid ${tokens.color.neutral[200]}`,
    },
    columnHeaderTitle: {
      fontWeight: 700,
      fontSize: "0.75rem",
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: tokens.color.neutral[600],
    },
    row: {
      "&:hover": {
        backgroundColor: tokens.color.neutral[50],
      },
      "&.Mui-selected": {
        backgroundColor: alpha(tokens.color.brand[600], 0.06),
      },
    },
    cell: {
      borderColor: tokens.color.neutral[200],
      fontSize: "0.875rem",
    },
    footerContainer: {
      borderTop: `1px solid ${tokens.color.neutral[200]}`,
      backgroundColor: tokens.color.neutral[0],
    },
  },
},

 
MuiAccordion: {
  styleOverrides: {
    root: {
      border: `1px solid ${tokens.color.neutral[200]}`,
      borderRadius: `${tokens.radius.md}px !important`,
      boxShadow: "none",
      "&:before": { display: "none" },
      "&.Mui-expanded": {
        margin: 0,
        borderColor: tokens.color.brand[600],
      },
    },
  },
},
MuiAccordionSummary: {
  styleOverrides: {
    root: {
      backgroundColor: tokens.color.neutral[50],
      borderRadius: tokens.radius.md,
      minHeight: 48,
      fontWeight: 600,
    },
  },
},

 
MuiAlert: {
  styleOverrides: {
    root: {
      borderRadius: tokens.radius.md,
      fontFamily: '"Nunito", sans-serif',
      fontWeight: 600,
    },
    standardInfo: {
      backgroundColor: alpha(tokens.color.info, 0.08),
      color: tokens.color.neutral[700],
    },
    standardSuccess: {
      backgroundColor: alpha(tokens.color.success, 0.1),
      color: tokens.color.success,
    },
    standardError: {
      backgroundColor: alpha(tokens.color.error, 0.1),
      color: tokens.color.error,
    },
    standardWarning: {
      backgroundColor: alpha(tokens.color.warning, 0.1),
      color: tokens.color.warning,
    },
  },
},

 
MuiDivider: {
  styleOverrides: {
    root: {
      borderColor: tokens.color.neutral[200],
    },
  },
},

 
MuiMenuItem: {
  styleOverrides: {
    root: {
      fontFamily: '"Nunito", sans-serif',
      fontSize: "0.875rem",
      borderRadius: tokens.radius.xs,
      "&:hover": {
        backgroundColor: tokens.color.neutral[100],
      },
      "&.Mui-selected": {
        backgroundColor: alpha(tokens.color.brand[600], 0.08),
        color: tokens.color.brand[600],
        fontWeight: 700,
      },
    },
  },
},

 
MuiSelect: {
  styleOverrides: {
    select: {
      fontFamily: '"Nunito", sans-serif',
      // backgroundColor: tokens.color.neutral[0],
    },
  },
},

  },
});

export default theme;
